import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { EVALS_API_ERROR_KEY, MILLISECONDS_IN_A_DAY } from '@/utils/constants';
import { EvalJobType } from '@/ux-common/src/types/api';
import { formatDateTo2DigitDayAndMonthWithYear } from '@/ux-common/src/utils/isoToStringDateFormat';

import { useEvals } from '@/hooks/useEvals';

import { Icons, System } from '../icons';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const EvalStatusPopover = ({
  appName,
  workflowName,
}: {
  appName?: string;
  workflowName?: string;
}) => {
  const {
    state,
    evaluateActions: { fetchEvalsJobs },
  } = useEvals();
  const evalJobs: EvalJobType[] = state.evalJobs;
  const { messages } = useLanguage();

  const [failedJobs, setFailedJobs] = useState<EvalJobType[]>([]);
  const [inProgressJobs, setInProgressJobs] = useState<EvalJobType[]>([]);
  const [showEvalStartedMessage, setShowEvalStartedMessage] = useState(false);
  const [apiErrors, setApiErrors] = useState<
    Array<{
      status: number;
      statusText: string;
      timestamp: number;
    }>
  >([]);

  // track currently running jobs
  const evaluationStartTime = useRef(0);

  // Store interval ID in ref so we can clear it anytime
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = React.useRef(false);

  // Load API errors from localStorage
  useEffect(() => {
    const savedErrors = localStorage.getItem(EVALS_API_ERROR_KEY);
    if (savedErrors) {
      const errorArray = JSON.parse(savedErrors);
      const twentyFourHoursAgo = Date.now() - MILLISECONDS_IN_A_DAY;
      const validErrors = errorArray.filter(
        (err: any) => err.timestamp > twentyFourHoursAgo // Show api failures from last 24 hours
      );

      if (validErrors.length > 0) {
        setApiErrors(validErrors);
        localStorage.setItem(EVALS_API_ERROR_KEY, JSON.stringify(validErrors));
      } else {
        localStorage.removeItem(EVALS_API_ERROR_KEY);
      }
    }
  }, []);

  useEffect(() => {
    const handleEvaluationStarted = () => {
      setShowEvalStartedMessage(true);
      evaluationStartTime.current = Date.now();
    };

    const handleEvaluationFailed = (event: any) => {
      setShowEvalStartedMessage(false);

      const newError = {
        status: event.detail.status ?? 500,
        statusText:
          event.detail.statusText ?? messages?.Evaluation?.fallback_status_text,
        timestamp: event.detail.timestamp ?? Date.now(),
      };

      // Update localStorage
      const existingErrors = JSON.parse(
        localStorage.getItem(EVALS_API_ERROR_KEY) || '[]'
      );
      const updatedErrors = [...existingErrors, newError];
      localStorage.setItem(EVALS_API_ERROR_KEY, JSON.stringify(updatedErrors));

      setApiErrors((prev) => [...prev, newError]);
    };

    const handleCustomEvalFinished = () => {
      setShowEvalStartedMessage(false);
    };

    // Listen for evaluation started and failed status
    window.addEventListener('evaluationStarted', handleEvaluationStarted);
    window.addEventListener('evaluationFailed', handleEvaluationFailed);
    window.addEventListener('customEvalFinished', handleCustomEvalFinished);
    return () => {
      window.removeEventListener('evaluationStarted', handleEvaluationStarted);
      window.removeEventListener('evaluationFailed', handleEvaluationFailed);
      window.removeEventListener(
        'customEvalFinished',
        handleCustomEvalFinished
      );
    };
  }, []);

  useEffect(() => {
    if (!evalJobs) return;

    const twentyFourHoursAgo = Date.now() - MILLISECONDS_IN_A_DAY;

    // state.evalJobs is shared across the layout, so scope it to this
    // popover's app/workflow to avoid leaking another app's job status.
    const scopedJobs = evalJobs.filter(
      (cur) => cur.metadata.app_name === appName
    );

    const failed = scopedJobs.filter(
      (cur) =>
        cur.status === 'FAILED' &&
        cur.end_time &&
        cur.end_time > twentyFourHoursAgo // Show failed jobs from last 24 hours
    );

    // PENDING jobs haven't been picked up yet but are still active - counting
    // only RUNNING here stopped polling (below) the moment the last RUNNING
    // job finished, even while a sibling was still queued as PENDING, which
    // left it stuck with no further status checks.
    const running = scopedJobs.filter(
      (cur) => cur.status === 'RUNNING' || cur.status === 'PENDING'
    );

    // Check if atleast 1 job has started after the evaluationStartTime
    if (running.some((job) => job.start_time > evaluationStartTime.current)) {
      setShowEvalStartedMessage(false);
    }

    setFailedJobs(failed);
    setInProgressJobs(running);

    if (running.length === 0 && intervalRef.current) {
      window.dispatchEvent(new Event('evalFromInsightsFinished'));
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [state.evalJobs, showEvalStartedMessage]);

  useEffect(() => {
    if (inProgressJobs.length > 0 && !intervalRef.current) {
      intervalRef.current = setInterval(
        async () => {
          if (isFetchingRef.current) {
            return;
          }

          isFetchingRef.current = true;

          try {
            await fetchEvalsJobs(appName || '', workflowName);
          } catch (err) {
            console.error('Error while fetching eval jobs:', err);
          } finally {
            isFetchingRef.current = false;
          }
        },
        (Number(process.env.NEXT_PUBLIC_EVAL_JOBS_TIME_IN_SEC) || 10) * 1000 // default to 10 seconds if undefined
      );
    }
  }, [inProgressJobs.length]);

  useEffect(() => {
    fetchEvalsJobs(appName || '', workflowName);
  }, []);

  // Stop polling when the popover unmounts (e.g. navigating away or the header
  // remounting) so the eval/jobs request doesn't keep firing in the background.
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  if (
    failedJobs.length === 0 &&
    inProgressJobs.length === 0 &&
    !showEvalStartedMessage &&
    apiErrors.length === 0
  )
    return;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <h1 className="text-body-subtle">{messages?.Evaluation.evaluation}:</h1>
        <TooltipProvider>
          <div className="flex items-center gap-4">
            {showEvalStartedMessage && (
              <h2 className="text-body-subtle">
                {messages?.Evaluation?.starting}
              </h2>
            )}

            {!showEvalStartedMessage &&
              (failedJobs.length > 0 || apiErrors.length > 0) && (
                <Tooltip
                  open={
                    failedJobs.length > 0 || apiErrors.length > 0
                      ? undefined
                      : false
                  }
                >
                  <TooltipTrigger
                    asChild
                    disabled={failedJobs.length === 0 && apiErrors.length === 0}
                  >
                    <div className="flex cursor-default items-center gap-1.5">
                      {System.FailedJobIcon('')}
                      <h2 className="text-body-subtle">
                        {failedJobs.length + apiErrors.length}{' '}
                        {messages?.Evaluation.failed}
                      </h2>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" sideOffset={4}>
                    <div className="max-h-[403px] w-[343px] overflow-y-auto px-2 py-3">
                      <div className="flex flex-col gap-3">
                        {/* top header */}
                        <div className="flex items-center gap-1">
                          {/* icon */}
                          {System.FailedJobIcon('')}
                          <h2 className="text-body-primary">
                            {messages?.Evaluation?.errors}
                          </h2>
                        </div>
                        <Separator orientation="horizontal" />

                        <div className="flex flex-col gap-4">
                          {[
                            ...apiErrors.map((err) => ({
                              type: 'api' as const,
                              timestamp: err.timestamp,
                              data: err,
                            })),
                            ...failedJobs.map((job) => ({
                              type: 'job' as const,
                              timestamp: job.end_time!,
                              data: job,
                            })),
                          ]
                            .sort((a, b) => b.timestamp - a.timestamp)
                            .map((cur, index) => (
                              <div
                                className="flex flex-col gap-1"
                                key={
                                  cur.type === 'api'
                                    ? `api-error-${index}`
                                    : cur.data.job_id
                                }
                              >
                                {cur.type === 'api' ? (
                                  <>
                                    <h2 className="text-body-primary">
                                      {messages?.Evaluation?.api_failure}
                                    </h2>
                                    <p className="text-body-sm text-body-subtle">
                                      {cur.data.status} {cur.data.statusText}
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex flex-col gap-2">
                                      <div className="flex flex-col gap-1">
                                        <h2 className="text-body-primary">
                                          {cur.data.metadata.template}
                                        </h2>
                                      </div>
                                    </div>
                                    <div className="flex items-center text-body-sm text-body-subtle">
                                      <p>
                                        {messages?.General.submitted}:{' '}
                                        {formatDateTo2DigitDayAndMonthWithYear(
                                          cur.data.start_time,
                                          { format: 'dd/mm/yy hh:mm aa' }
                                        )}
                                      </p>
                                      <Separator
                                        className="mx-2 h-3 bg-line-secondary"
                                        orientation="vertical"
                                      />
                                      <p>
                                        {messages?.General.failed}:{' '}
                                        {formatDateTo2DigitDayAndMonthWithYear(
                                          cur.data.end_time!,
                                          { format: 'dd/mm/yy hh:mm aa' }
                                        )}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}

            {!showEvalStartedMessage && inProgressJobs.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex cursor-default items-center gap-1.5">
                    {Icons.StaticProgressIcon('size-5')}
                    <h2 className="text-body-subtle">
                      {inProgressJobs.length} {messages?.Evaluation.in_progress}
                    </h2>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" sideOffset={4}>
                  <div className="max-h-[403px] w-[400px] overflow-y-auto px-2 py-3">
                    <div className="flex flex-col gap-3">
                      {/* top header */}
                      <div className="flex items-center gap-1">
                        {/* icon */}
                        {System.EvaluationIcon('')}
                        <h2 className="text-body-primary">
                          {messages?.Evaluation?.evaluations}
                        </h2>
                      </div>
                      <Separator orientation="horizontal" />

                      <div className="flex flex-col gap-4">
                        {/* array of in-progress jobs */}
                        {inProgressJobs?.map((cur, index, array) => (
                          <>
                            <div
                              className="flex flex-col gap-1"
                              key={cur.job_id}
                            >
                              <p className="text-body-sm text-body-subtle">
                                {formatDateTo2DigitDayAndMonthWithYear(
                                  cur.start_time
                                )}
                              </p>
                              <div className="flex flex-col gap-2">
                                <div className="flex flex-col gap-1">
                                  <h2 className="text-body-primary">
                                    {cur.metadata.template}
                                  </h2>
                                  {cur.metadata.breakdown_filter != '' && (
                                    <div>
                                      <p className="text-body-sm text-form-input-disabled">
                                        {messages?.Evaluation.breakdown}:
                                      </p>
                                      <p className="text-form-input-text text-body-subtle">
                                        {cur.metadata.breakdown_filter}
                                      </p>
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-1">
                                    {/* calendar */}
                                    <div className="flex items-center justify-center">
                                      {System.CalendarIcon('')}
                                    </div>
                                    <p className="text-body-sm text-body-subtle">
                                      {`${formatDateTo2DigitDayAndMonthWithYear(
                                        cur.metadata.start_time_filter
                                      )} - ${formatDateTo2DigitDayAndMonthWithYear(
                                        cur.metadata.end_time_filter
                                      )}`}
                                    </p>
                                  </div>
                                  <div className="flex gap-1">
                                    {/* lens */}
                                    <div>{System.MagnifierIcon('')}</div>
                                    <p className="text-body-sm text-body-subtle">
                                      {messages?.Evaluation.info_message}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {index < array.length - 1 && (
                              <Separator orientation="horizontal" />
                            )}
                          </>
                        ))}
                      </div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      </div>
    </>
  );
};

export default EvalStatusPopover;
